import React, { useEffect, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Streams a tailored, on-the-fly findings narrative in the copilot voice,
// grounded strictly on the graph facts. Types out for a "thinking out loud" feel.
export const SynthesisNarrative = ({ sessionId, company, assumptions, facts, coverage, revision = 0 }) => {
  const [text, setText] = useState("");
  const [done, setDone] = useState(false);
  const ranFor = useRef(-1);

  useEffect(() => {
    if (ranFor.current === revision) return;
    ranFor.current = revision;
    setText("");
    setDone(false);
    // Without graph facts we have nothing grounded to synthesise, so be honest
    // rather than let the model guess. The visitor can tailor via the copilot.
    if (!facts || !Array.isArray(facts.frameworks) || facts.frameworks.length === 0) {
      const who = company && company !== "your company" ? company : "your business";
      setText(`I could not automatically retrieve enough public records to map ${who} in detail. The findings below are general starting points, not a tailored assessment. Tell me in the chat what you do and where your customers are, and I will refine everything to your exact situation.`);
      setDone(true);
      return;
    }
    (async () => {
      try {
        const resp = await fetch(`${API}/synthesis`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId,
            company,
            assumptions,
            facts,
            coverage: (coverage || []).map((c) => ({ name: c.name, status: c.status, framework: c.art })),
          }),
        });
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let full = "";
        while (true) {
          const { done: d, value } = await reader.read();
          if (d) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop();
          for (const part of parts) {
            const line = part.trim();
            if (!line.startsWith("data:")) continue;
            try {
              const data = JSON.parse(line.slice(5).trim());
              if (data.delta) {
                full += data.delta;
                setText(full);
              }
            } catch (e) { /* ignore partial SSE JSON chunk */ }
          }
        }
      } catch (e) {
        setText("We have mapped your compliance landscape. Your key findings are below.");
      } finally {
        setDone(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision]);

  return (
    <p className="mt-4 text-mutedink text-[15.5px] leading-relaxed whitespace-pre-line" data-testid="synthesis-narrative">
      {text || (revision > 0 ? "Re-assessing your situation with your correction" : "Reading what is public and mapping your exact situation")}
      {!done && <span className="inline-block w-[2px] h-[1.05em] align-[-0.15em] ml-0.5 bg-pink animate-pulse" />}
    </p>
  );
};
